import type Model from "@ember-data/model";
import { camelize } from "@ember/string";
import type ForcJournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import type JournalEventModel from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import { getLastJeOfClass } from "parog-web/utils/journal-events";
import ControlTower from "wise-control-tower";

/**
 * Retrieve the last JournalEvent of the given type.
 */
function lastJournalEvent<T extends JournalEventModel>(this: {
  patient: Patient;
  journalEventClass: { new (): T };
}): T | undefined {
  const jeFromRelationship =
    this.patient[
      camelize((this.journalEventClass as unknown as typeof Model).modelName)
    ];
  return (
    jeFromRelationship ||
    getLastJeOfClass(this.patient.activeJournalEvents, this.journalEventClass)
  );
}

/**
 * Retrieve the JournalEvent from the given sourceJeId query parameter.
 */
function sourceJournalEvent<T extends JournalEventModel>(
  this: ForcJournalEventBasedComponent
): T | undefined {
  if (!this.sourceJeId) return;
  const record = this.store.peekRecord(
    this.journalEventClassType,
    this.sourceJeId
  ) as T;

  if (!record) {
    const error = new Error(
      `ForcAlert: The sourceJournalEvent '${this.journalEventClass.type}' corresponding to the id '${this.sourceJeId}' was not found. Returning no JE.`
    );
    ControlTower.captureException(error);
    console.error(error);
  }

  return record;
}

/**
 * This strategy tries to fetch the sourceJeId. If not found or not given, that
 * JE is returned.
 * @return JournalEvent
 */
function sourceOrLastJournalEvent<T extends JournalEventModel>(
  this: ForcJournalEventBasedComponent
): T {
  return this.sourceJeId
    ? sourceJournalEvent.call(this)
    : lastJournalEvent.call(this);
}

export default {
  lastJournalEvent,
  sourceJournalEvent,
  sourceOrLastJournalEvent
};
